import { useState, useEffect, useMemo, useRef } from "react";
import { anzeigeName } from "@/lib/paketHinweise";
import {
  ereignis,
  TRICHTER,
  pixelKlickAusgang,
  pixelCheckoutGestartet,
} from "@/lib/tracking";
import Head from "next/head";
import { useRouter } from "next/router";
import Header from "@/components/common/Header";
import Footer from "@/components/common/Footer";
import CookieBanner from "@/components/CookieBanner";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { loadOfferView, listVisibleOfferSlugs } from "@/lib/offerView";
import { getPaperStorageSizeRating } from "@/components/products/ProductListElementQuality";
import DatePicker from "@/components/common/DatePicker";
import Stornobedingungen from "@/components/fotobox/Stornobedingungen";
import BildModal, { BildLink } from "@/components/fotobox/BildModal";
import AngebotDetails from "@/components/anlass/AngebotDetails";
import FaqSection from "@/components/sections/FaqSection";
import { angebotFaq } from "@/utils/angebotFaq";
import {
  EVENT_TYPES,
  DELIVERY_METHODS,
  calculateTotals,
  formatEUR,
} from "@/lib/bookingModel";

const GUEST_OPTIONS = [25, 50, 75, 100, 150, 200, 201];

// Vorlauf wie bei der Polaroid-Buchung: Der Vermieter braucht Zeit für die
// Zusage, dann kommt die Anzahlung, dann Versand oder Anfahrt.
const MIN_LEAD_DAYS = 7;

const guestLabel = (n) => (n === 201 ? "mehr als 200" : `ca. ${n}`);

/** Date → "yyyy-MM-dd" ohne Zeitzonen-Verschiebung. */
function toIsoDay(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

const addDays = (date, n) => {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
};

/* ------------------------------------------------------------- Bausteine */

/** Verlinkt den Text, sofern eine URL vorliegt — sonst bleibt er einfach Text. */
function Link({ href, label }) {
  if (!href) return <>{label}</>;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="underline hover:text-slate-900"
    >
      {label}
    </a>
  );
}

function Chip({ active, children, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full px-4 py-2 text-sm transition ${
        active
          ? "bg-[#FF5744] text-white"
          : "bg-white text-slate-700 ring-1 ring-slate-300 hover:bg-slate-50"
      }`}
    >
      {children}
    </button>
  );
}

function Field({ label, hint, error, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-slate-800 mb-1.5">
        {label}
      </span>
      {children}
      {error ? (
        <span className="block text-xs text-red-600 mt-1.5">{error}</span>
      ) : (
        hint && (
          <span className="block text-xs text-slate-500 mt-1.5">{hint}</span>
        )
      )}
    </label>
  );
}

const inputClass =
  "w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:border-[#FF5744] focus:outline-none focus:ring-2 focus:ring-[#FF5744]/30";

function Stepper({ value, onChange, max }) {
  // maxPerBooking ist mindestens 1; fehlt der Wert, ist eins die sichere
  // Annahme — sonst ließe sich unbegrenzt hochzählen.
  const obergrenze = Math.max(1, Number(max) || 1);
  const amEnde = value >= obergrenze;
  const amAnfang = value <= 0;

  return (
    <div className="flex items-center gap-2">
      <Schritt
        aus={amAnfang}
        onClick={() => onChange(Math.max(0, value - 1))}
        label="weniger"
      >
        –
      </Schritt>
      <span className="w-6 text-center text-sm font-medium tabular-nums">
        {value}
      </span>
      <Schritt
        aus={amEnde}
        onClick={() => onChange(Math.min(obergrenze, value + 1))}
        label="mehr"
      >
        +
      </Schritt>
    </div>
  );
}

/**
 * Ein Knopf des Zählers.
 *
 * An der Grenze wird er ausgegraut und abgeschaltet, statt klickbar
 * auszusehen und nichts zu tun — bei „max 1“ ist das Plus nach dem ersten
 * Klick sinnlos, bei 0 das Minus.
 */
function Schritt({ aus, onClick, label, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={aus}
      aria-label={label}
      className={`h-8 w-8 rounded-full ring-1 transition ${
        aus
          ? "cursor-not-allowed ring-slate-200 text-slate-300"
          : "ring-slate-300 text-slate-600 hover:bg-slate-50"
      }`}
    >
      {children}
    </button>
  );
}

/** Drucker-Bewertung abhängig von der Gästezahl — wie im Vergleich. */
function PrinterRating({ printer, guestCount }) {
  if (!printer) return null;
  const rating = getPaperStorageSizeRating(
    printer.paperStorageSize,
    printer.printTimeSeconds,
    guestCount,
    (key) => key
  );
  const labels = {
    "rating.VeryGood": ["Sehr gut", "text-green-700 bg-green-50"],
    "rating.Good": ["Gut", "text-green-700 bg-green-50"],
    "rating.Okay": ["Okay", "text-amber-700 bg-amber-50"],
    "rating.NotIdeal": ["Knapp", "text-orange-700 bg-orange-50"],
    "rating.NoInfo": ["Keine Info", "text-slate-600 bg-slate-100"],
  };
  const [text, cls] = labels[rating.rating] || labels["rating.NoInfo"];

  return (
    <div className="mt-2 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">
      <span className={`rounded-full px-2 py-0.5 font-medium ${cls}`}>
        Drucker: {text}
      </span>{" "}
      <span>
        {printer.printTimeSeconds} Sek. pro Foto · Papierwechsel nach{" "}
        {printer.paperStorageSize} Ausdrucken
      </span>
    </div>
  );
}

/* ------------------------------------------------------------- Seite */

export default function FotoboxCheckout({
  offer: offerProp,
  vorschau = false,
  eigenesAngebot = null,
}) {
  const router = useRouter();
  // Der Slug steht in der URL, die Anfrage läuft weiter über die interne ID.
  const offerId = offerProp?.id;

  const [offer] = useState(offerProp);
  const [loadError] = useState(offerProp ? "" : "Angebot nicht gefunden");
  /**
   * Der Schritt steht in der Adresse, nicht nur im Speicher.
   *
   * Vorher lag er in useState: Wer bei „Deine Angaben" den Zurück-Knopf des
   * Browsers drückte, landete nicht bei der Paketauswahl, sondern auf der
   * Seite davor — die Eingaben waren weg. Mit dem Schritt als
   * Adressbestandteil (flach, ohne Neuladen) funktioniert Zurück wie erwartet.
   */
  const step = router.query.schritt === "2" ? 2 : 1;

  const setStep = (ziel) => {
    const query = { ...router.query };
    if (ziel === 2) query.schritt = "2";
    else delete query.schritt;

    router.push({ pathname: router.pathname, query }, undefined, {
      shallow: true,
      scroll: false,
    });
  };

  /**
   * Der Einstieg in den Trichter.
   *
   * Ohne diese Zahl lässt sich nicht unterscheiden, ob niemand auf die
   * Angebotsseiten kommt oder ob sie kommen und wieder gehen — von außen
   * sieht beides gleich aus (keine Buchung).
   */
  const gesehenGemeldet = useRef(null);
  useEffect(() => {
    if (!offerProp?.id) return;
    // Genau einmal je Angebot.
    //
    // React ruft Effekte im strengen Modus doppelt auf, und beim Zurück im
    // Browser wird die Komponente erneut eingebunden. Ein doppelt gezählter
    // Trichtereintritt halbiert jede Quote — und zwar still, man sieht der
    // Zahl nichts an.
    if (gesehenGemeldet.current === offerProp.id) return;
    gesehenGemeldet.current = offerProp.id;

    ereignis(TRICHTER.ANGEBOT_GESEHEN, {
      angebot: offerProp.id,
      anbieter: offerProp.partnerName,
      abPreis: offerProp.booth?.price,
      pakete: offerProp.packages?.length || 0,
    });
    // Nur beim Öffnen, nicht bei jedem Schrittwechsel — der Schritt steht in
    // der Adresse, die Seite selbst wird dabei nicht neu geladen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerProp?.id]);

  const [guestCount, setGuestCount] = useState(50);
  const [packageId, setPackageId] = useState(null);
  const [extraQty, setExtraQty] = useState({});

  // Das Datum liegt als Date-Objekt vor (der DatePicker arbeitet damit) und
  // wird erst beim Absenden zu "yyyy-MM-dd".
  const [eventDate, setEventDate] = useState(null);

  const [form, setForm] = useState({
    zip: "",
    city: "",
    firstName: "",
    email: "",
    phone: "",
    eventType: "",
    deliveryMethod: "",
    acceptedTerms: false,
  });
  // Welches Extra-Bild gerade groß gezeigt wird ({ bild, titel } oder null).
  const [bildGross, setBildGross] = useState(null);
  const [errors, setErrors] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  /**
   * Vorauswahl aus den Angebotsdaten: Versand, sonst Lieferung, sonst Abholung.
   *
   * Setzt bewusst nur, was noch nicht gesetzt ist. Vorher hat der Effekt jedes
   * Mal überschrieben — und da er an `router.query` hängt, lief er beim
   * Schrittwechsel erneut: Wer bei „Deine Angaben" zurückging, fand statt
   * seines Pakets wieder das erste vor.
   */
  useEffect(() => {
    if (!offerProp) return;
    const guests = Number(router.query.guests);
    if (guests && GUEST_OPTIONS.includes(guests)) setGuestCount(guests);

    setPackageId((vorher) => vorher || offerProp.packages[0]?.id || null);

    const d = offerProp.delivery;
    setForm((f) =>
      f.deliveryMethod
        ? f
        : {
            ...f,
            deliveryMethod: d.post
              ? DELIVERY_METHODS.POST
              : d.selfDelivery
              ? DELIVERY_METHODS.DELIVERY
              : d.pickup
              ? DELIVERY_METHODS.PICKUP
              : "",
          }
    );
  }, [offerProp, router.query.guests]);

  const selectedPackage = useMemo(
    () => offer?.packages.find((p) => p.id === packageId) || null,
    [offer, packageId]
  );

  // Extras, die nur zum gewählten Paket gehören.
  const selectedExtras = useMemo(() => {
    if (!selectedPackage) return [];
    return selectedPackage.optional
      .map((e) => ({ ...e, quantity: extraQty[e.id] || 0 }))
      .filter((e) => e.quantity > 0);
  }, [selectedPackage, extraQty]);

  // Anfahrtskosten nachschlagen, sobald die PLZ vollständig ist. Nur bei
  // Lieferung durch den Anbieter — Versand und Abholung haben keine Anfahrt.
  const [travel, setTravel] = useState(null);
  useEffect(() => {
    if (
      !offerId ||
      form.deliveryMethod !== DELIVERY_METHODS.DELIVERY ||
      !/^\d{5}$/.test(form.zip)
    ) {
      setTravel(null);
      return;
    }
    let abgebrochen = false;
    fetch(
      `/api/fotobox/travel-cost?offerId=${encodeURIComponent(offerId)}&zip=${
        form.zip
      }`
    )
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        if (!abgebrochen) setTravel(j);
      })
      .catch(() => {});
    return () => {
      abgebrochen = true;
    };
  }, [offerId, form.zip, form.deliveryMethod]);

  // Was die Lieferung kostet, hängt an der gewählten Art: Versandpauschale,
  // berechnete Anfahrt oder nichts bei Abholung.
  const deliveryCost = useMemo(() => {
    if (!offer) return 0;
    if (form.deliveryMethod === DELIVERY_METHODS.POST) {
      return offer.delivery.post?.costs || 0;
    }
    if (form.deliveryMethod === DELIVERY_METHODS.DELIVERY) {
      return travel?.known && travel.inRange ? travel.cost : 0;
    }
    return 0;
  }, [offer, form.deliveryMethod, travel]);

  const totals = useMemo(
    () =>
      calculateTotals({
        boothPrice: offer?.booth.price || 0,
        packagePrice: selectedPackage?.price || 0,
        extras: selectedExtras,
        deliveryCost,
      }),
    [offer, selectedPackage, selectedExtras, deliveryCost]
  );

  /** Wie die Lieferart im Warenkorb steht — immer sichtbar, nicht nur bei Anfahrt. */
  const deliverySummary = useMemo(() => {
    if (!offer || !form.deliveryMethod) return null;
    if (form.deliveryMethod === DELIVERY_METHODS.POST) {
      return {
        label: "Versand per Post",
        note: `Ankunft ${offer.delivery.post.daysBeforeStart}–${offer.delivery.post.daysBeforeEnd} Tage vorher`,
        value: deliveryCost === 0 ? "kostenlos" : formatEUR(deliveryCost),
      };
    }
    if (form.deliveryMethod === DELIVERY_METHODS.PICKUP) {
      return {
        label: "Selbst abholen",
        note: `${offer.delivery.pickup.zip} ${offer.delivery.pickup.city}`,
        value: "kostenlos",
      };
    }
    return {
      label: "Lieferung durch den Anbieter",
      note: !/^\d{5}$/.test(form.zip)
        ? "PLZ eingeben für die Anfahrt"
        : !travel
        ? "wird berechnet…"
        : !travel.known
        ? "Anfahrt rechnet der Anbieter ab"
        : !travel.inRange
        ? `${travel.distanceKm} km — außerhalb`
        : `${travel.distanceKm} km Anfahrt`,
      value:
        travel?.known && travel.inRange
          ? travel.cost === 0
            ? "kostenlos"
            : formatEUR(travel.cost)
          : "–",
    };
  }, [offer, form.deliveryMethod, form.zip, travel, deliveryCost]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Liegt der Kunde außerhalb des Liefergebiets, ist die Anfrage sinnlos —
  // der Anbieter müsste sie ohnehin ablehnen.
  const outOfRange = Boolean(
    travel?.known &&
      !travel.inRange &&
      form.deliveryMethod === DELIVERY_METHODS.DELIVERY
  );

  const submit = async () => {
    setSubmitting(true);
    setErrors([]);
    try {
      const res = await fetch("/api/fotobox/request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          offerId,
          packageId,
          guestCount,
          eventDate: eventDate ? toIsoDay(eventDate) : "",
          extras: selectedExtras.map((e) => ({
            id: e.id,
            quantity: e.quantity,
          })),
          ...form,
        }),
      });
      const json = await res.json();
      if (!res.ok) {
        setErrors(json.errors || ["Es ist ein Fehler aufgetreten"]);
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
      ereignis(TRICHTER.ANFRAGE_GESENDET, {
        angebot: offerId,
        buchung: json.bookingId,
        // Vorgangsnummer für Google Ads: Ohne sie zählt ein zweiter Aufruf
        // desselben Vorgangs als zweite Conversion. Die Anfrage ist seit
        // dem 2026-10-06 eine sekundäre Conversion ohne Wert — Ads lernt
        // daran früh, optimiert aber weiter auf die bezahlte Buchung.
        transaction_id: json.bookingId,
        paket: selectedPackage?.name,
        gesamt: totals?.total,
        gaeste: guestCount,
        // Die Anzahlung steht hier bewusst nicht: Sie hängt an der Provision,
        // und die kennt der Browser nicht — sie gehört dem Vermieter, nicht
        // dem Kunden. calculateTotals liefert hier deshalb immer 0. Eine
        // Kennzahl, die wie eine Zahl aussieht und keine ist, richtet in
        // Berichten mehr Schaden an als eine fehlende. Der Betrag steht in
        // der Buchung und kommt aus der Datenbank.
      });
      setDone(json.bookingId);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      setErrors([e.message]);
    } finally {
      setSubmitting(false);
    }
  };

  if (loadError) {
    return (
      <Shell vorschau={vorschau} eigenesAngebot={eigenesAngebot}>
        <div className="rounded-2xl bg-white p-8 ring-1 ring-slate-200 text-center">
          <h1 className="text-xl font-bold text-slate-900">
            Dieses Angebot ist nicht verfügbar
          </h1>
          <p className="mt-2 text-slate-600">{loadError}</p>
          <a
            href="/fotobox-vergleich"
            className="mt-6 inline-block rounded-full bg-[#FF5744] px-5 py-2.5 text-sm font-semibold text-white"
          >
            Andere Fotoboxen ansehen
          </a>
        </div>
      </Shell>
    );
  }

  if (!offer) {
    return (
      <Shell vorschau={vorschau} eigenesAngebot={eigenesAngebot}>
        <p className="text-slate-500">Lädt…</p>
      </Shell>
    );
  }

  if (done) {
    return (
      <Shell vorschau={vorschau} eigenesAngebot={eigenesAngebot} title="Anfrage gesendet" offer={offer}>
        <div className="rounded-2xl bg-white p-8 ring-1 ring-slate-200">
          <div className="inline-flex items-center rounded-full bg-green-50 px-3 py-1 text-xs font-semibold text-green-700">
            Anfrage {done}
          </div>
          <h1 className="mt-4 text-2xl font-bold text-slate-900">
            Deine Anfrage ist raus
          </h1>
          <p className="mt-3 text-slate-700 leading-relaxed">
            {offer.partnerName} prüft jetzt, ob die Fotobox an deinem Miettag
            frei ist. Sobald zugesagt wird, bekommst du eine E-Mail an{" "}
            <strong>{form.email}</strong> mit dem Link zur Anzahlung.
          </p>
          <div className="mt-6 rounded-xl bg-slate-50 p-5 ring-1 ring-slate-200">
            <p className="text-sm font-medium text-slate-900">Und dann?</p>
            <ol className="mt-2 space-y-1.5 text-sm text-slate-700">
              <li>
                1. Der Anbieter sagt zu oder ab (meist innerhalb eines Tages)
              </li>
              <li>
                2. Bei Zusage zahlst du die Anzahlung und sicherst dir den
                Termin
              </li>
              <li>
                3. Ihr bekommt gegenseitig eure Kontaktdaten und klärt die
                Details
              </li>
            </ol>
          </div>
          <p className="mt-6 text-sm text-slate-500">
            Bis zur Anzahlung ist alles unverbindlich — es entstehen dir keine
            Kosten.
          </p>
        </div>
      </Shell>
    );
  }

  const d = offer.delivery;
  const deliveryOptions = [
    d.post && {
      key: DELIVERY_METHODS.POST,
      title: "Versand per Post",
      detail: `Ankunft ${d.post.daysBeforeStart}–${d.post.daysBeforeEnd} Tage vor deinem Event`,
      price: d.post.costs ? formatEUR(d.post.costs) : "kostenlos",
    },
    d.selfDelivery && {
      key: DELIVERY_METHODS.DELIVERY,
      title: "Lieferung durch den Anbieter",
      detail: `Aufbau ${
        d.selfDelivery.daysBeforeEvent === 0
          ? "am Veranstaltungstag"
          : `${d.selfDelivery.daysBeforeEvent} Tag(e) vorher`
      }, ab ${d.selfDelivery.zip} ${d.selfDelivery.city}`,
      // Sobald die PLZ steht, zeigen wir den echten Betrag statt „kommt noch“.
      price: !/^\d{5}$/.test(form.zip)
        ? "zzgl. Anfahrt"
        : !travel
        ? "…"
        : !travel.known
        ? "Anfahrt separat"
        : !travel.inRange
        ? "nicht möglich"
        : travel.cost === 0
        ? "Anfahrt kostenlos"
        : `zzgl. ${formatEUR(travel.cost)}`,
    },
    d.pickup && {
      key: DELIVERY_METHODS.PICKUP,
      title: "Selbst abholen",
      detail: `Bei ${d.pickup.zip} ${d.pickup.city}`,
      price: "kostenlos",
    },
  ].filter(Boolean);

  return (
    <Shell vorschau={vorschau} eigenesAngebot={eigenesAngebot} offer={offer}>
      {/* Fortschritt */}
      <ol className="mb-6 flex items-center gap-3 text-sm">
        {["Deine Auswahl", "Deine Angaben"].map((label, i) => {
          const n = i + 1;
          // Ein bereits erledigter Schritt lässt sich anklicken. Nur zurück,
          // nicht vor: Schritt 2 zu überspringen ergäbe nichts, solange kein
          // Paket gewählt ist.
          const zurueck = n < step;
          return (
            <li key={label} className="flex items-center gap-2">
              <button
                type="button"
                onClick={zurueck ? () => setStep(n) : undefined}
                disabled={!zurueck}
                aria-current={step === n ? "step" : undefined}
                className={`flex items-center gap-2 rounded-lg ${
                  zurueck ? "cursor-pointer hover:opacity-80" : "cursor-default"
                }`}
              >
                <span
                  className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${
                    step >= n
                      ? "bg-[#FF5744] text-white"
                      : "bg-slate-200 text-slate-600"
                  }`}
                >
                  {n}
                </span>
                <span
                  className={
                    step === n
                      ? "font-semibold text-slate-900"
                      : zurueck
                      ? "text-slate-600 underline decoration-slate-300 underline-offset-2"
                      : "text-slate-500"
                  }
                >
                  {label}
                </span>
              </button>
              {n === 1 && <span className="mx-1 text-slate-300">›</span>}
            </li>
          );
        })}
      </ol>

      {errors.length > 0 && (
        <div className="mb-5 rounded-xl bg-red-50 p-4 ring-1 ring-red-200">
          <p className="text-sm font-medium text-red-900">
            Bitte prüfe deine Angaben:
          </p>
          <ul className="mt-1.5 list-inside list-disc text-sm text-red-800">
            {errors.map((e, i) => (
              <li key={i}>{e}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        <div className="space-y-5">
          {step === 1 ? (
            <>
              {/* Anbieter & Ausstattung */}
              <section className="rounded-2xl bg-white p-5 ring-1 ring-slate-200">
                <div className="flex gap-5">
                  {offer.booth.image && (
                    // Die Fotobox-Bilder sind durchgehend 180×250 — im
                    // quadratischen Rahmen stand das Motiv zwischen grauen
                    // Balken. Rahmen im selben Verhältnis, Hintergrund weiß.
                    <img
                      src={offer.booth.image}
                      alt={`${offer.booth.name} von ${offer.partnerName}`}
                      className="aspect-[18/25] w-28 shrink-0 rounded-xl bg-white object-contain ring-1 ring-slate-200"
                    />
                  )}
                  <div className="min-w-0">
                    <p className="text-xs uppercase tracking-wide text-slate-500">
                      Fotobox-Anbieter
                    </p>
                    {/* Logo neben den Namen: Der Kunde bucht bei einem echten
                        Betrieb, nicht bei einem Eintrag in einer Liste. */}
                    <div className="flex items-center gap-2.5">
                      {offer.partnerLogo && (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img
                          src={offer.partnerLogo}
                          alt=""
                          className="h-9 w-9 shrink-0 rounded-full bg-white object-contain ring-1 ring-slate-200"
                        />
                      )}
                      <h1 className="text-xl font-bold text-slate-900">
                        {offer.partnerName}
                      </h1>
                    </div>
                    <p className="mt-1 text-sm text-slate-600">
                      {[
                        offer.booth.camType,
                        offer.booth.lightType,
                        offer.booth.screenSize
                          ? `${offer.booth.screenSize}" Bildschirm`
                          : null,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </p>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {[
                        offer.booth.controls?.liveView && "Live-View",
                        offer.booth.controls?.touch && "Touchscreen",
                        offer.booth.controls?.remote && "Fernbedienung",
                        offer.booth.digital?.gallery && "Online-Galerie",
                        offer.booth.digital?.usb && "USB-Stick",
                        offer.booth.digital?.phone && "Fotos aufs Handy",
                        offer.booth.digital?.ai && "AI-Fotos",
                      ]
                        .filter(Boolean)
                        .map((f) => (
                          <span
                            key={f}
                            className="rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-700"
                          >
                            {f}
                          </span>
                        ))}
                    </div>
                  </div>
                </div>

                {/* Hier vergleicht der Kunde noch und hat keinen Termin
                    gewählt — deshalb steht die Regel da ("bis 22 Tage vor dem
                    Miettag") und kein Datum. Beide Zusagen sichtbar: Absagen
                    und Verschieben sind zwei verschiedene Fragen. Die
                    kostenlose Umbuchung stand früher als eigener grüner
                    Kasten hier und ist jetzt Teil derselben Auskunft. */}
                <Stornobedingungen
                  cancellation={offer.cancellation}
                  partnerName={offer.partnerName}
                  gesamtpreis={totals.total}
                  agbUrl={offer.agbUrl}
                  variante="karte"
                />
              </section>

              {/* Gästezahl + Pakete */}
              <section className="rounded-2xl bg-white p-5 ring-1 ring-slate-200">
                <h2 className="font-bold text-slate-900">
                  {offer.weiterleitung
                    ? `Die Pakete von ${offer.partnerName}`
                    : "Wähle dein Paket"}
                </h2>
                {/* Nur wo es einen Drucker zu bewerten gibt.
                    Bei einem rein digitalen Angebot — vier der 61 sichtbaren
                    am 2026-08-24 — versprach der Satz eine Bewertung von
                    etwas, das gar nicht dabei ist. Die Gästezahl bleibt
                    trotzdem: Sie steht im Warenkorb und geht mit in die
                    Anfrage, der Vermieter sieht sie in seiner Mail. */}
                {offer.packages.some((p) => p.printer) && (
                  <p className="mt-1 text-sm text-slate-600">
                    Anhand der Gästeanzahl bewerten wir den Drucker.
                  </p>
                )}

                <div className="mt-3 flex flex-wrap gap-2">
                  {GUEST_OPTIONS.map((g) => (
                    <Chip
                      key={g}
                      active={guestCount === g}
                      onClick={() => setGuestCount(g)}
                    >
                      {guestLabel(g)} Gäste
                    </Chip>
                  ))}
                </div>

                <div className="mt-5 space-y-3">
                  {offer.packages.map((pkg) => {
                    const active = pkg.id === packageId;
                    return (
                      <button
                        key={pkg.id}
                        type="button"
                        onClick={() => {
                          setPackageId(pkg.id);
                          // Nur der Klick, nicht die Vorauswahl beim Laden —
                          // sonst hätte jeder Seitenaufruf ein „gewählt".
                          ereignis(TRICHTER.PAKET_GEWAEHLT, {
                            angebot: offerId,
                            paket: pkg.name,
                            paketPreis: pkg.price,
                            gaeste: guestCount,
                          });
                        }}
                        className={`block w-full rounded-xl p-4 text-left ring-1 transition ${
                          active
                            ? "bg-[#FF5744]/5 ring-[#FF5744]"
                            : "bg-white ring-slate-200 hover:bg-slate-50"
                        }`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="min-w-0">
                            <p className="font-semibold text-slate-900">
                              {pkg.name}
                            </p>
                            <ul className="mt-1.5 space-y-1">
                              {pkg.contents.map((c, i) => (
                                <li
                                  key={i}
                                  className="flex flex-wrap items-center gap-1.5 text-sm text-slate-700"
                                >
                                  {/* Jede Zeile beginnt gleich. Vorher stand
                                      dort, wo ein Bild hinterlegt war, ein
                                      Vorschaubild statt des Häkchens — dann
                                      ließ sich die Liste nicht mehr
                                      überfliegen. Das Bild gibt es jetzt auf
                                      Klick. */}
                                  <span>✓ {anzeigeName(c)}</span>
                                  {c.image && (
                                    <BildLink
                                      onClick={() =>
                                        setBildGross({
                                          bild: c.image,
                                          titel: c.name,
                                        })
                                      }
                                    />
                                  )}
                                  {/* Die Druck-Flatrate ist ein Kaufargument
                                      und kein Nebensatz — als Auszeichnung
                                      erkennt man sie beim Überfliegen. */}
                                  {/* Die Stückzahl an einem eigenen Namen.
                                      „Requisitenbox" sagt nicht, wie viel
                                      drin ist; „50 Requisiten" schon. Wo der
                                      Name die Zahl bereits trägt, bleibt die
                                      Auszeichnung weg — sonst steht sie
                                      zweimal da. */}
                                  {(c.category === "Requisiten" ||
                                    c.category === "Stundenpaket") &&
                                  c.itemCount &&
                                  !nennt(anzeigeName(c), c.itemCount) ? (
                                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
                                      {c.itemCount}{" "}
                                      {c.category === "Stundenpaket"
                                        ? "Stunden"
                                        : "Stück"}
                                    </span>
                                  ) : null}
                                  {c.category === "Fotodruck" &&
                                    (c.isPrintFlatrate ? (
                                      <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700 ring-1 ring-emerald-200">
                                        Flatrate
                                      </span>
                                    ) : c.prints && !nennt(c.name, c.prints) ? (
                                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
                                        {c.prints} Ausdrucke
                                      </span>
                                    ) : null)}
                                  {/* Die Beschreibung, klein darunter.
                                      Bei Requisiten trägt der Name nur noch
                                      Kategorie und Stückzahl — was genau
                                      drin ist, steht hier. Bisher wurde sie
                                      nur bei zubuchbaren Extras gezeigt. */}
                                  {c.description && (
                                    <span className="mt-0.5 block text-xs font-normal leading-snug text-slate-500">
                                      {c.description}
                                    </span>
                                  )}
                                </li>
                              ))}
                              {!pkg.contents.length && (
                                <li className="text-sm text-slate-500">
                                  Nur die Fotobox, ohne Zusatzleistungen
                                </li>
                              )}
                            </ul>
                            <PrinterRating
                              printer={pkg.printer}
                              guestCount={guestCount}
                            />
                          </div>
                          <div className="shrink-0 text-right">
                            <p className="text-lg font-bold text-slate-900">
                              {formatEUR(offer.booth.price + pkg.price)}
                            </p>
                            <p className="text-xs text-slate-500">gesamt</p>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </section>

              {/* Optionale Extras */}
              {selectedPackage?.optional?.length > 0 && (
                <section className="rounded-2xl bg-white p-5 ring-1 ring-slate-200">
                  <h2 className="font-bold text-slate-900">
                    Wähle deine Extras
                  </h2>
                  <div className="mt-3 space-y-3">
                    {selectedPackage.optional.map((e) => (
                      <div
                        key={e.id}
                        className="flex items-start justify-between gap-4 rounded-xl p-3 ring-1 ring-slate-200"
                      >
                        <div className="min-w-0">
                          <p className="flex flex-wrap items-center gap-x-2 font-medium text-slate-900">
                            <span>
                              {e.name}{" "}
                              <span className="text-slate-500">
                                {formatEUR(e.price)}
                              </span>
                            </span>
                            {e.image && (
                              <BildLink
                                onClick={() =>
                                  setBildGross({ bild: e.image, titel: e.name })
                                }
                              />
                            )}
                          </p>
                          {e.description && (
                            <p className="mt-0.5 text-sm text-slate-600">
                              {e.description}
                            </p>
                          )}
                        </div>
                        <Stepper
                          value={extraQty[e.id] || 0}
                          max={e.maxPerBooking}
                          onChange={(v) =>
                            setExtraQty((q) => ({ ...q, [e.id]: v }))
                          }
                        />
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </>
          ) : (
            <>
              {/* Schritt 2: Angaben */}
              <section className="rounded-2xl bg-white p-5 ring-1 ring-slate-200">
                <h2 className="font-bold text-slate-900">Mietinformation</h2>

                <div className="mt-4 max-w-sm">
                  <Field label="Wann brauchst du die Fotobox?">
                    <DatePicker
                      id="eventDate"
                      value={eventDate}
                      onChange={setEventDate}
                      min={addDays(new Date(), MIN_LEAD_DAYS)}
                      max={addDays(new Date(), 730)}
                    />
                  </Field>
                </div>

                <div className="mt-5">
                  <p className="mb-2 text-sm font-medium text-slate-800">
                    Was für ein Event planst du?
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {EVENT_TYPES.map((t) => (
                      <Chip
                        key={t}
                        active={form.eventType === t}
                        onClick={() => setField("eventType", t)}
                      >
                        {t}
                      </Chip>
                    ))}
                  </div>
                </div>

                <div className="mt-5 grid gap-4 sm:grid-cols-2">
                  <Field label="PLZ des Veranstaltungsorts">
                    <input
                      className={inputClass}
                      inputMode="numeric"
                      maxLength={5}
                      value={form.zip}
                      onChange={(e) =>
                        setField("zip", e.target.value.replace(/\D/g, ""))
                      }
                    />
                  </Field>
                  <Field label="Ort">
                    <input
                      className={inputClass}
                      value={form.city}
                      onChange={(e) => setField("city", e.target.value)}
                    />
                  </Field>
                </div>

                <div className="mt-5">
                  <p className="mb-2 text-sm font-medium text-slate-800">
                    Wie möchtest du die Fotobox bekommen?
                  </p>
                  <div className="space-y-2">
                    {deliveryOptions.map((o) => (
                      <label
                        key={o.key}
                        className={`flex cursor-pointer items-start gap-3 rounded-xl p-3 ring-1 transition ${
                          form.deliveryMethod === o.key
                            ? "bg-[#FF5744]/5 ring-[#FF5744]"
                            : "ring-slate-200 hover:bg-slate-50"
                        }`}
                      >
                        <input
                          type="radio"
                          name="delivery"
                          className="mt-1 h-4 w-4 accent-[#FF5744]"
                          checked={form.deliveryMethod === o.key}
                          onChange={() => setField("deliveryMethod", o.key)}
                        />
                        <span className="flex-1">
                          <span className="block text-sm font-medium text-slate-900">
                            {o.title}
                          </span>
                          <span className="block text-sm text-slate-600">
                            {o.detail}
                          </span>
                        </span>
                        <span className="shrink-0 text-sm text-slate-500">
                          {o.price}
                        </span>
                      </label>
                    ))}
                  </div>

                  {form.deliveryMethod === DELIVERY_METHODS.DELIVERY &&
                    d.selfDelivery?.ranges?.length > 0 && (
                      <div
                        className={`mt-3 rounded-lg px-4 py-3 text-sm ring-1 ${
                          outOfRange
                            ? "bg-red-50 ring-red-200"
                            : "bg-slate-50 ring-slate-200"
                        }`}
                      >
                        {!/^\d{5}$/.test(form.zip) ? (
                          <p className="text-slate-600">
                            Gib deine PLZ ein — dann rechnen wir die
                            Anfahrtskosten aus und zeigen dir den Endpreis.
                          </p>
                        ) : !travel ? (
                          <p className="text-slate-500">
                            Anfahrt wird berechnet…
                          </p>
                        ) : !travel.known ? (
                          <p className="text-slate-600">
                            Für deine PLZ können wir die Entfernung nicht
                            bestimmen — der Anbieter rechnet die Anfahrt separat
                            mit dir ab.
                          </p>
                        ) : !travel.inRange ? (
                          <p className="text-red-700">
                            <strong>Zu weit weg: {travel.distanceKm} km</strong>{" "}
                            — {offer.partnerName} liefert nur bis{" "}
                            {travel.radiusKm} km.{" "}
                            {deliveryOptions.length > 1
                              ? "Wähle oben eine andere Option."
                              : "Such dir bitte eine andere Fotobox."}
                          </p>
                        ) : (
                          <p className="text-slate-700">
                            <strong>{travel.distanceKm} km</strong> bis zu dir —
                            Anfahrt{" "}
                            <strong>
                              {travel.cost === 0
                                ? "kostenlos"
                                : formatEUR(travel.cost)}
                            </strong>
                            . Ist bereits im Gesamtpreis enthalten.
                          </p>
                        )}
                      </div>
                    )}
                </div>
              </section>

              <section className="rounded-2xl bg-white p-5 ring-1 ring-slate-200">
                <h2 className="font-bold text-slate-900">Kontaktinformation</h2>
                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                  <Field label="Dein Vorname">
                    <input
                      className={inputClass}
                      value={form.firstName}
                      onChange={(e) => setField("firstName", e.target.value)}
                    />
                  </Field>
                  <Field
                    label="Deine E-Mail Adresse"
                    hint="Hierhin schicken wir dir die Antwort des Vermieters."
                  >
                    <input
                      type="email"
                      className={inputClass}
                      value={form.email}
                      onChange={(e) => setField("email", e.target.value)}
                    />
                  </Field>
                  <Field
                    label="Deine Telefonnummer"
                    hint="Wird für Rückfragen vom Anbieter benötigt."
                  >
                    <input
                      className={inputClass}
                      value={form.phone}
                      onChange={(e) => setField("phone", e.target.value)}
                    />
                  </Field>
                </div>
              </section>
            </>
          )}
        </div>

        {/* Warenkorb */}
        {/* Sieben der Angebote werden beim Anbieter selbst gebucht; wir
            vermitteln nur. Dort wäre ein Warenkorb irreführend — er führte
            in einen Checkout, den es für dieses Angebot nicht gibt. Die
            Seite existiert trotzdem, weil sie zu den Markensuchen ranken
            soll; sie zeigt die Angaben und führt dann weiter. */}
        {offer.weiterleitung ? (
          <aside className="lg:sticky lg:top-6 h-fit">
            <div className="rounded-2xl bg-white p-5 ring-1 ring-slate-200">
              <h2 className="font-bold text-slate-900">
                Buchung bei {offer.partnerName}
              </h2>
              <p className="mt-2 text-sm leading-6 text-slate-600">
                Dieses Angebot wird direkt bei {offer.partnerName} gebucht.
                Wir haben die Angaben zusammengetragen und verglichen — den
                Vertrag schließt ihr dort ab.
              </p>
              {offer.fromPrice > 0 && (
                <p className="mt-4 text-2xl font-bold text-slate-900">
                  ab {Math.round(offer.fromPrice)} €
                </p>
              )}
              <a
                // Über /go/: Der Klick bekommt eine ID für den Partner und
                // wir die Herkunft dazu (lib/klickAusgang).
                href={`/go/${offer.slug}`}
                onClick={pixelKlickAusgang}
                target="_blank"
                // nofollow, damit keine Autorität an den Partner abfließt.
                // Kein noreferrer: Der Partner soll sehen, woher der Klick
                // kam. Dieselbe Auszeichnung wie beim Clickout im Vergleich.
                rel="nofollow noopener"
                className="mt-4 block w-full rounded-full bg-[#FF5744] px-5 py-3 text-center text-sm font-semibold text-white transition hover:brightness-105"
              >
                Zu {offer.partnerName}
              </a>
              <p className="mt-3 text-xs text-slate-500">
                Öffnet die Seite von {offer.partnerName} in einem neuen Tab.
              </p>
            </div>
          </aside>
        ) : (
        <aside className="lg:sticky lg:top-6 h-fit">
          <div className="rounded-2xl bg-white p-5 ring-1 ring-slate-200">
            <h2 className="font-bold text-slate-900">Dein Warenkorb</h2>
            <p className="mt-1 text-xs text-slate-500">
              für {guestLabel(guestCount)} Gäste
              {step === 2 && (
                <button
                  type="button"
                  onClick={() => {
                    setStep(1);
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                  className="ml-1.5 underline hover:text-slate-800"
                >
                  ändern
                </button>
              )}
            </p>
            <dl className="mt-4 space-y-2.5 text-sm">
              <div className="flex justify-between gap-4">
                <dt className="min-w-0 text-slate-600">{offer.booth.name}</dt>
                <dd className="shrink-0 whitespace-nowrap font-medium tabular-nums">
                  {formatEUR(offer.booth.price)}
                </dd>
              </div>
              {selectedPackage && (
                <div className="flex justify-between gap-4">
                  <dt className="min-w-0 text-slate-600">
                    Paket {selectedPackage.name}
                  </dt>
                  <dd className="shrink-0 whitespace-nowrap font-medium tabular-nums">
                    {formatEUR(selectedPackage.price)}
                  </dd>
                </div>
              )}
              {selectedExtras.map((e) => (
                <div key={e.id} className="flex justify-between gap-4">
                  <dt className="min-w-0 text-slate-600">
                    {e.quantity}× {e.name}
                  </dt>
                  <dd className="shrink-0 whitespace-nowrap font-medium tabular-nums">
                    {formatEUR(e.price * e.quantity)}
                  </dd>
                </div>
              ))}
              {deliverySummary && (
                <div className="flex justify-between gap-4">
                  <dt className="min-w-0 text-slate-600">
                    {deliverySummary.label}
                    <span className="block text-xs text-slate-400">
                      {deliverySummary.note}
                    </span>
                  </dt>
                  <dd className="shrink-0 whitespace-nowrap font-medium tabular-nums">
                    {deliverySummary.value}
                  </dd>
                </div>
              )}
              <div className="flex justify-between gap-4 border-t border-slate-200 pt-3">
                <dt className="font-semibold text-slate-900">Gesamtpreis</dt>
                <dd className="text-lg font-bold text-slate-900">
                  {formatEUR(totals.total)}
                </dd>
              </div>
            </dl>

            {step === 1 ? (
              <button
                type="button"
                onClick={() => {
                  ereignis(TRICHTER.ANGABEN_ERREICHT, {
                    angebot: offerId,
                    paket: selectedPackage?.name,
                    gesamt: totals?.total,
                    gaeste: guestCount,
                    extras: selectedExtras.length,
                  });
                  // Hier entscheidet sich der Kunde für unseren Ablauf statt
                  // für den Clickout — das Gegenstück zum Ausgang.
                  pixelCheckoutGestartet();
                  setStep(2);
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
                disabled={!packageId}
                className="mt-5 w-full rounded-full bg-[#FF5744] px-5 py-3 text-sm font-semibold text-white disabled:opacity-40"
              >
                Weiter zum letzten Schritt
              </button>
            ) : (
              <>
                <label className="mt-5 flex items-start gap-2.5 text-xs text-slate-600">
                  <input
                    type="checkbox"
                    className="mt-0.5 h-4 w-4 accent-[#FF5744]"
                    checked={form.acceptedTerms}
                    onChange={(e) =>
                      setField("acceptedTerms", e.target.checked)
                    }
                  />
                  <span>
                    Ich akzeptiere <Link href={offer.agbUrl} label="AGB" /> und{" "}
                    <Link href={offer.privacyUrl} label="Datenschutz" /> vom
                    Fotobox-Vermieter {offer.partnerName} sowie die{" "}
                    <Link href="/agb" label="AGB" /> und den{" "}
                    <Link href="/datenschutz" label="Datenschutz" /> des
                    Vermittlers FotoboxGuru.
                  </span>
                </label>

                <button
                  type="button"
                  onClick={submit}
                  disabled={submitting || outOfRange}
                  className="mt-4 w-full rounded-full bg-[#FF5744] px-5 py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-slate-300"
                >
                  {submitting
                    ? "Wird gesendet…"
                    : outOfRange
                    ? "Außerhalb des Liefergebiets"
                    : "Fotobox unverbindlich anfragen"}
                </button>
                {outOfRange && (
                  <p className="mt-2 text-xs text-red-700">
                    {offer.partnerName} liefert nicht bis {form.zip}.
                  </p>
                )}
                {/* Direkt unter den Knopf, weil hier die Sorge sitzt: Der
                    Kunde hat gerade alles ausgefüllt und zögert. "Was, wenn
                    was dazwischenkommt?" gehört hierhin, nicht 800 Pixel
                    weiter oben. Der Termin ist inzwischen gewählt, also steht
                    da ein echtes Datum statt einer Vorlaufzeit.

                    Bewusst VOR dem Hinweis auf die Unverbindlichkeit: Dass
                    noch nichts abgebucht wird, steht schon auf dem Knopf. Die
                    Stornoregelung ist die neue Auskunft und bekommt deshalb
                    den ersten Platz. */}
                <Stornobedingungen
                  cancellation={offer.cancellation}
                  partnerName={offer.partnerName}
                  eventDate={eventDate}
                  gesamtpreis={totals.total}
                  agbUrl={offer.agbUrl}
                  variante="warenkorb"
                />

                <p className="mt-2.5 text-center text-xs text-slate-500">
                  Unverbindliche Anfrage — jetzt wird noch nichts abgebucht.
                </p>

                {/* Der Ablauf ist ungewohnt: Der Kunde drückt einen Knopf und
                    zahlt eben NICHT sofort. Ohne Erklärung wirkt das entweder
                    beunruhigend ("was passiert jetzt?") oder unverbindlich im
                    schlechten Sinn ("da kommt wohl nichts"). Deshalb die vier
                    Schritte im Klartext — mit den echten Beträgen statt einer
                    vagen Andeutung. */}
                <div className="mt-5 rounded-xl bg-slate-50 p-4 ring-1 ring-slate-200">
                  <h3 className="text-sm font-semibold text-slate-900">
                    Was passiert als Nächstes?
                  </h3>
                  <ol className="mt-3 space-y-3">
                    {[
                      [
                        "Wir fragen für dich an",
                        `${offer.partnerName} bekommt deine Anfrage sofort per E-Mail. Du bekommst eine Bestätigung mit allen Angaben.`,
                      ],
                      [
                        "Antwort in 24 Stunden",
                        "Der Anbieter prüft, ob dein Termin frei ist, und sagt zu oder ab. Sagt er ab, kostet dich das nichts.",
                      ],
                      [
                        "Erst dann zahlst du",
                        "Bei einer Zusage bekommst du einen Zahlungslink über eine kleine Anzahlung. Damit ist dein Termin verbindlich gebucht.",
                      ],
                      [
                        "Details klärt ihr direkt",
                        `Ihr bekommt gegenseitig die Kontaktdaten. Die Restzahlung geht direkt an ${offer.partnerName}, nicht über uns.`,
                      ],
                    ].map(([titel, text], i) => (
                      <li key={titel} className="flex gap-3">
                        <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#FF5744] text-[11px] font-bold text-white">
                          {i + 1}
                        </span>
                        <span className="text-xs leading-relaxed text-slate-600">
                          <strong className="block text-slate-900">
                            {titel}
                          </strong>
                          {text}
                        </span>
                      </li>
                    ))}
                  </ol>
                </div>
              </>
            )}
          </div>
        </aside>
        )}
      </div>

      {/* Unter der Buchungsstrecke, nicht darüber: Wer buchen will, soll
          nicht erst an Erklärungen vorbeiscrollen. Die Seiten hatten 167
          Wörter — Überschrift, Pakete, Warenkorb — obwohl zu jedem Angebot
          Angaben vorliegen, die sonst niemand veröffentlicht: die
          Anfahrtsstaffel in Kilometern und die Stornostaffel in Tagen. */}
      <AngebotDetails angebot={offer} />

      <div className="pt-10 lg:pt-14">
        <FaqSection
          title={`Häufige Fragen zu ${offer.partnerName}`}
          subtitle="Beantwortet aus den Angaben dieses Anbieters."
          items={angebotFaq(offer)}
        />
      </div>

      {bildGross && (
        <BildModal
          bild={bildGross.bild}
          titel={bildGross.titel}
          onClose={() => setBildGross(null)}
        />
      )}
    </Shell>
  );
}

/**
 * Die Leiste, mit der der Vermieter sein Angebot einreicht.
 *
 * Eingereicht wird nicht von hier aus, sondern im Backend: Dort liegt sein
 * Anmeldetoken, dort steht die Liste mit dem, was noch fehlt, und dort will
 * er ohnehin hin, wenn etwas zu korrigieren ist. Diese Leiste schickt ihn
 * also mit der Absicht zurück — der Umweg ist keiner.
 */
function EinreichenLeiste({ offerId }) {
  const [laeuft, setLaeuft] = useState(false);

  const einreichen = () => {
    setLaeuft(true);
    window.location.href = `/partner?einreichen=${encodeURIComponent(offerId)}`;
  };

  return (
    <>
      {/* Platzhalter in der Höhe der Leiste — ohne ihn liegt sie über dem
          Abbinder, und das Impressum ist nicht mehr anklickbar. */}
      <div className="h-24" aria-hidden="true" />
      <div className="fixed inset-x-0 bottom-0 z-50 border-t border-slate-200 bg-white/95 shadow-[0_-4px_16px_rgba(0,0,0,0.06)] backdrop-blur">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-4 py-3">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-900">
              So sieht dein Angebot für Kunden aus.
            </p>
            <p className="text-xs text-slate-500">
              Noch nicht öffentlich — nur du siehst diese Seite.
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-3">
            <a
              href="/partner"
              className="text-sm font-medium text-slate-600 underline decoration-slate-300 underline-offset-2 hover:text-slate-900"
            >
              Zurück zum Bearbeiten
            </a>
            <button
              type="button"
              onClick={einreichen}
              disabled={laeuft}
              className="rounded-full bg-[#FF5744] px-5 py-2 text-sm font-semibold text-white transition hover:brightness-95 disabled:opacity-50"
            >
              {laeuft ? "…" : "Zur Prüfung einreichen"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

function Shell({ children, title, offer, vorschau = false, eigenesAngebot = null }) {
  // Angebotsseiten sollen gefunden werden — deshalb kein noindex, sondern
  // echte Metadaten. Wer nach dem Anbieternamen sucht, soll hier landen.
  const metaTitle = offer
    ? `${offer.partnerName} Fotobox mieten ab ${Math.round(
        offer.fromPrice
      )} € | FotoboxGuru`
    : title
    ? `${title} | FotoboxGuru`
    : "Fotobox mieten | FotoboxGuru";

  const metaDesc = offer
    ? `Fotobox von ${offer.partnerName} mieten: ${[
        offer.booth.camType,
        offer.booth.lightType,
        offer.packages.length > 1
          ? `${offer.packages.length} Pakete`
          : offer.packages[0]?.name,
      ]
        .filter(Boolean)
        .join(", ")}. Ab ${Math.round(
        offer.fromPrice
      )} € — unverbindlich anfragen.`
    : "Fotobox mieten und direkt anfragen.";

  return (
    <>
      <Head>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDesc} />
        {/* Eine Vorschau ist nichts für Suchmaschinen — erreichbar ist sie
            ohnehin nur mit dem Admin-Cookie, aber doppelt hält besser. */}
        {vorschau && <meta name="robots" content="noindex,nofollow" />}
        {offer && (
          <link
            rel="canonical"
            href={`https://fotoboxguru.de/fotobox-mieten/${offer.slug}`}
          />
        )}
        {offer && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "Product",
                name: `Fotobox mieten bei ${offer.partnerName}`,
                // Ohne Beschreibung meldet die Search Console das
                // Produkt-Snippet als unvollständig. Sie kommt aus denselben
                // Feldern wie die Meta-Beschreibung, damit sie nicht
                // auseinanderlaufen.
                description: metaDesc,
                // Seit die Bilder im Storage liegen, sind die Adressen
                // absolut. Der frühere Präfix ergab
                // "https://fotoboxguru.dehttps://…" — für Google unbrauchbar.
                image: offer.booth.image
                  ? offer.booth.image.startsWith("http")
                    ? offer.booth.image
                    : `https://fotoboxguru.de${offer.booth.image}`
                  : undefined,
                brand: { "@type": "Brand", name: offer.partnerName },
                offers: {
                  "@type": "Offer",
                  price: offer.fromPrice,
                  priceCurrency: "EUR",
                  availability: "https://schema.org/InStock",
                },
              }),
            }}
          />
        )}
        {offer && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                itemListElement: [
                  {
                    "@type": "ListItem",
                    position: 1,
                    name: "Fotobox mieten",
                    item: "https://fotoboxguru.de/",
                  },
                  {
                    "@type": "ListItem",
                    position: 2,
                    name: "Fotobox-Vergleich",
                    item: "https://fotoboxguru.de/fotobox-vergleich",
                  },
                  {
                    "@type": "ListItem",
                    position: 3,
                    name: offer.partnerName,
                    item: `https://fotoboxguru.de/fotobox-mieten/${offer.slug}`,
                  },
                ],
              }),
            }}
          />
        )}
      </Head>
      <div className="min-h-screen bg-[#F9F9F9]">
        {/* In der Vorschau muss auf den ersten Blick klar sein, dass das
            nicht die öffentliche Seite ist — sonst hält man ein noch nicht
            freigegebenes Angebot für live und wundert sich, warum es
            niemand findet. */}
        {vorschau && !eigenesAngebot && (
          <div className="sticky top-0 z-[60] flex flex-wrap items-center justify-center gap-x-3 gap-y-1 bg-amber-400 px-4 py-2 text-center text-sm font-medium text-amber-950">
            <span>Vorschau — dieses Angebot ist noch nicht freigegeben.</span>
            <a
              href="/api/admin-fotobox/vorschau?ende=1"
              className="underline underline-offset-2 hover:no-underline"
            >
              Vorschau beenden
            </a>
          </div>
        )}
        {/* Im Checkout klebt der Header nicht.
            Auf Inhaltsseiten ist er ein Angebot: zurück zur Suche, zum
            Vergleich, zur Startseite. Hier ist der Kunde angekommen und füllt
            aus — dann nimmt er nur Platz weg, und auf dem Telefon ist das die
            Zeile, in der die Preise stehen sollten. Außerdem stritt er sich
            mit der Vorschauleiste um dieselbe oberste Kante. */}
        <Header sticky={false} />
        <main className="mx-auto max-w-6xl px-4 py-8 lg:py-12">{children}</main>
        <Footer />
        {/* Der Vermieter sieht sein eigenes Angebot vor dem Einreichen.
            Die Leiste klebt unten, weil die Entscheidung ans Ende der
            Seite gehört: erst alles ansehen, dann einreichen. Und sie
            lässt Platz darunter frei, sonst verdeckt sie den Abbinder. */}
        {eigenesAngebot && <EinreichenLeiste offerId={eigenesAngebot} />}
      </div>
      {/* In der eigenen Vorschau kein Cookie-Banner: Er sitzt unten und legt
          sich genau über die Leiste zum Einreichen. Eine
          Einwilligungsabfrage ergibt hier ohnehin nichts — die Seite ist
          noindex, nur der Vermieter sieht sie, und er entscheidet über
          Tracking nicht für sich, sondern schaut sich seine eigene Auslage
          an. */}
      {!eigenesAngebot && <CookieBanner />}
    </>
  );
}


/** Trägt der Name die Anzahl schon? Dann braucht es die Auszeichnung nicht. */
function nennt(name, anzahl) {
  return new RegExp(`\\b${anzahl}\\b`).test(name || "");
}

export async function getStaticPaths() {
  // Alle sichtbaren Angebote vorab erzeugen, damit Suchmaschinen und Kunden
  // eine fertige Seite bekommen. Neue Angebote werden bei Bedarf nachgerendert.
  const slugs = await listVisibleOfferSlugs();
  return {
    paths: slugs.map((slug) => ({ params: { offerId: slug } })),
    fallback: "blocking",
  };
}

export async function getStaticProps({ params, locale, preview, previewData }) {
  // „preview" setzt der Admin-Bereich über /api/admin-fotobox/vorschau.
  // Nur dann werden Angebote ausgeliefert, die noch nicht freigegeben sind.
  const { offer, error } = await loadOfferView(params.offerId, {
    vorschau: Boolean(preview),
  });

  if (error || !offer) {
    // Eine Minute war zu kurz: Seit ein Angebot offline gehen kann, bleiben
    // seine Links bestehen, und jeder Besuch nach Ablauf der Frist schreibt
    // die 404-Seite neu in Vercels Zwischenspeicher (Absprache 2026-10-03).
    return { notFound: true, revalidate: 3600 };
  }

  return {
    props: {
      offer,
      vorschau: Boolean(preview),
      // Nur der Vermieter selbst bekommt die Leiste zum Einreichen — der
      // Admin sieht dieselbe Vorschau, hat aber seine eigenen Wege. Die
      // Angebots-ID steht in den Vorschaudaten, die Next signiert; sie lässt
      // sich also nicht von außen setzen.
      eigenesAngebot:
        previewData?.offerId && previewData.offerId === offer.id
          ? previewData.offerId
          : null,
      ...(await serverSideTranslations(locale, ["common"])),
    },
    // Preise und Verfügbarkeit ändern sich selten — stündlich reicht.
// Frist von einer Stunde auf einen Tag (Absprache 2026-10-03):
// Jede Neuerzeugung schreibt die Seite in Vercels Zwischenspeicher, und
// abgerechnet wird in Blöcken zu 8 KB. Eine Seite von ~300 KB kostet so
// rund 36 Einheiten — pro Erzeugung. Stündlich erneuert reicht eine
// Handvoll gecrawlter Seiten, um das Monatskontingent von 200.000
// aufzubrauchen; am 2026-10-03 waren 75 % verbraucht, und bei 100 %
// schaltet Vercel das Projekt ab.
//
// Inhaltlich kostet es nichts: Was sich hier ändert, hängt am Tagesstand
// (daySeed, stand) und wechselt ohnehin nur einmal am Tag.
    revalidate: 86400,
  };
}
